import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { getStoredSkillBuilderSettings } from 'src/helpers/utils';
import type { GetAdminSkillTreeResponse } from 'src/openapi';
import {
  getQuestionAIAssistantChats,
  questionAIAssistantQuery,
  aiGuideFeedback,
  getAdminSkillTree,
  getJoinedMultiplayerRooms,
  joinMultiplayerSession,
  exitMultiplayerRoom,
  deleteMultiplayerRoom,
  transferRoomAdmin,
  getCourseInfo,
  getCourseMap,
  getCourseProgress,
  purchaseGuidedCourse,
  submitCourseReview,
  deleteCourseReview,
  dismissCourseReview,
  enrollGuidedCourse,
  getIntroContent,
  completeIntro,
  getLabelContent,
  completeGuidedLabel,
  theoryAiChat,
  getTheoryAiChats,
  startGuidedLabelSession,
  getGuidedSessionProgress,
  getGuidedCurrentQuestion,
  attemptGuidedQuestion,
  startGuidedTopicSession,
  getTopicTestProgress,
  closeTopicTestSession,
  closeTopicJumpSession,
} from '../actions/skillBuilder';
import type {
  AdminSkillTreeModuleStats,
  QuestionAIAssistantSocketResponse,
  SkillBuilderState,
  TheoryAISocketResponse,
} from '../types/skillBuilder';

const storedBuilderSettings = getStoredSkillBuilderSettings();

const initialState: SkillBuilderState = {
  skillBuilderSettings: storedBuilderSettings,
  questionAIAssistantChats: null,
  sendingAIAssistantQuery: false,
  isAIAssistantWorking: false,
  adminSkillTree: null,
  adminSkillTreeModuleStats: null as AdminSkillTreeModuleStats | null,
  adminSkillTreeSelectionCount: {},
  multiplayerRooms: {},
  guidedCourseInfo: null,
  isFetchingGuidedCourseInfo: false,
  guidedCourseMap: null,
  isFetchingGuidedCourseMap: false,
  guidedCourseProgress: null,
  isFetchingGuidedCourseProgress: false,
  isEnrollingGuidedCourse: false,
  labelContent: null,
  isFetchingLabelContent: false,
  labelContentError: null,
  introContent: null,
  isFetchingIntroContent: false,
  introContentError: null,
  isCompletingLabel: false,
  labelCompletion: null,
  theoryAIChats: null,
  isSendingTheoryAIQuery: false,
  isTheoryAIWorking: false,
  guidedSession: null,
  isStartingGuidedSession: false,
  guidedSessionProgress: null,
  isFetchingGuidedSessionProgress: false,
  guidedCurrentQuestion: null,
  isFetchingGuidedCurrentQuestion: false,
  guidedAttemptResult: null,
  isSubmittingGuidedAttempt: false,
  topicTestSession: null,
  isStartingTopicTestSession: false,
  topicTestProgress: null,
  isFetchingTopicTestProgress: false,
  topicTestResult: null,
  isClosingTopicTestSession: false,
};

/* eslint-disable no-param-reassign */
const skillBuilderSlice = createSlice({
  name: 'skillBuilder',
  initialState,
  reducers: {
    changeSkillBuilderSetting: (
      state,
      action: PayloadAction<{ key: string; value: boolean }>,
    ) => {
      const { key, value } = action.payload;
      localStorage.setItem(key, JSON.stringify(value));
      state.skillBuilderSettings = {
        ...state.skillBuilderSettings,
        [key]: value,
      };
    },
    onAIAssistantSocketResponse: (
      state,
      action: PayloadAction<QuestionAIAssistantSocketResponse>,
    ) => {
      if (
        !state.questionAIAssistantChats?.question_id ||
        state.questionAIAssistantChats.question_id !==
          action.payload.response?.question_id
      ) {
        return;
      }
      state.questionAIAssistantChats.chats =
        state.questionAIAssistantChats.chats?.map((chat) => {
          if (
            action.payload.response &&
            chat.id === action.payload.response.id
          ) {
            state.isAIAssistantWorking = false;
            return action.payload.response;
          }
          return chat;
        });
    },
    onTheoryAISocketResponse: (
      state,
      action: PayloadAction<TheoryAISocketResponse>,
    ) => {
      if (!state.theoryAIChats || !action.payload.response) return;
      state.theoryAIChats.chats = state.theoryAIChats.chats?.map((chat) => {
        if (chat.id === action.payload.response!.id) {
          state.isTheoryAIWorking = false;
          return action.payload.response!;
        }
        return chat;
      });
    },
    resetTheoryAIChats: (state) => {
      state.theoryAIChats = null;
      state.isTheoryAIWorking = false;
      state.isSendingTheoryAIQuery = false;
    },
    resetSkillBuilderState: () => initialState,
  },
  extraReducers(builder) {
    builder.addCase(getQuestionAIAssistantChats.pending, (state) => {
      state.questionAIAssistantChats = null;
    });
    builder.addCase(getQuestionAIAssistantChats.fulfilled, (state, action) => {
      state.questionAIAssistantChats = action.payload;
    });
    builder
      .addCase(questionAIAssistantQuery.pending, (state) => {
        state.sendingAIAssistantQuery = true;
      })
      .addCase(questionAIAssistantQuery.fulfilled, (state, action) => {
        if (
          !state.questionAIAssistantChats?.chats ||
          action.payload.response?.question_id !==
            state.questionAIAssistantChats.question_id
        ) {
          return;
        }

        if (action.payload.response) {
          state.questionAIAssistantChats.chats.push(action.payload.response);
        }
        state.sendingAIAssistantQuery = false;
        state.isAIAssistantWorking = true;
      })
      .addCase(questionAIAssistantQuery.rejected, (state) => {
        state.sendingAIAssistantQuery = false;
      });
    builder.addCase(aiGuideFeedback.fulfilled, (state, action) => {
      const {
        aIAssistantFeedbackBody: { feedback },
        chatId,
      } = action.meta.arg;
      if (state.questionAIAssistantChats) {
        state.questionAIAssistantChats.chats =
          state.questionAIAssistantChats.chats?.map((chat) => {
            if (chat.id === chatId) {
              return {
                ...chat,
                is_liked: feedback === 'like',
                is_disliked: feedback === 'dislike',
              };
            }
            return chat;
          });
      }
    });
    builder.addCase(getAdminSkillTree.fulfilled, (state, action) => {
      const payload = action.payload as GetAdminSkillTreeResponse;
      // module_stats typed as AdminSkillTreeModuleStats | object | undefined
      state.adminSkillTreeModuleStats =
        (payload.module_stats as AdminSkillTreeModuleStats) ?? null;
      state.adminSkillTree = (payload.skill_tree ?? [])
        .filter((item) => item.sub_skill?.length)
        .map((item) => {
          let totalCount = 0;
          let solvedCount = 0;

          item.sub_skill?.forEach((skill) => {
            totalCount += skill.total_question_count ?? 0;
            solvedCount += skill.user_solved_count ?? 0;
          });

          return {
            data: {
              ...item.data,
              total_question_count: totalCount,
              user_solved_count: solvedCount,
            },
            sub_skill: item.sub_skill,
          };
        });
      state.adminSkillTreeSelectionCount =
        payload.skill_tree?.reduce<typeof state.adminSkillTreeSelectionCount>(
          (prev, curr) => ({
            ...prev,
            [curr.data!.id!]: {
              selectedSubCount:
                curr.sub_skill?.filter((subSkill) => subSkill.is_selected)
                  .length ?? 0,
              totalSubCount: curr.sub_skill?.length ?? 0,
            },
          }),
          {},
        ) ?? {};
    });
    builder.addCase(
      getJoinedMultiplayerRooms.fulfilled,
      (state, { payload }) => {
        if (payload.page === 1) {
          state.multiplayerRooms = payload;
        } else {
          state.multiplayerRooms.rooms?.push(...(payload.rooms ?? []));
          state.multiplayerRooms.page = payload.page;
          state.multiplayerRooms.total_pages = payload.total_pages;
        }
      },
    );
    builder.addCase(joinMultiplayerSession.fulfilled, (state, action) => {
      const newRoom = action.payload;
      const roomId = newRoom.id;
      if (!state.multiplayerRooms.rooms) {
        state.multiplayerRooms.rooms = [newRoom];
        return;
      }
      const index = state.multiplayerRooms.rooms.findIndex(
        (room) => room.id === roomId,
      );
      if (index !== -1) {
        state.multiplayerRooms.rooms[index] = newRoom;
      } else {
        state.multiplayerRooms.rooms.unshift(newRoom);
      }
    });
    builder.addCase(exitMultiplayerRoom.fulfilled, (state, action) => {
      const { roomId } = action.meta.arg;
      if (state.multiplayerRooms.rooms) {
        state.multiplayerRooms.rooms = state.multiplayerRooms.rooms.filter(
          (room) => room.id !== roomId,
        );
      }
    });
    builder.addCase(deleteMultiplayerRoom.fulfilled, (state, action) => {
      const { roomId } = action.meta.arg;
      if (state.multiplayerRooms.rooms) {
        state.multiplayerRooms.rooms = state.multiplayerRooms.rooms.filter(
          (room) => room.id !== roomId,
        );
      }
    });
    builder.addCase(transferRoomAdmin.fulfilled, (state, action) => {
      const { roomId } = action.meta.arg;
      const newAdmin = action.payload?.new_admin;
      if (state.multiplayerRooms.rooms && newAdmin) {
        const room = state.multiplayerRooms.rooms.find((r) => r.id === roomId);
        if (room) {
          room.admin = newAdmin;
        }
      }
    });
    builder
      .addCase(getCourseInfo.pending, (state) => {
        state.isFetchingGuidedCourseInfo = true;
      })
      .addCase(getCourseInfo.fulfilled, (state, action) => {
        state.isFetchingGuidedCourseInfo = false;
        state.guidedCourseInfo = action.payload;
      })
      .addCase(getCourseInfo.rejected, (state) => {
        state.isFetchingGuidedCourseInfo = false;
      });
    builder
      .addCase(getCourseMap.pending, (state) => {
        state.isFetchingGuidedCourseMap = true;
      })
      .addCase(getCourseMap.fulfilled, (state, action) => {
        state.isFetchingGuidedCourseMap = false;
        state.guidedCourseMap = action.payload;
      })
      .addCase(getCourseMap.rejected, (state) => {
        state.isFetchingGuidedCourseMap = false;
      });
    builder
      .addCase(getCourseProgress.pending, (state) => {
        state.isFetchingGuidedCourseProgress = true;
      })
      .addCase(getCourseProgress.fulfilled, (state, action) => {
        state.isFetchingGuidedCourseProgress = false;
        state.guidedCourseProgress = action.payload;
      })
      .addCase(getCourseProgress.rejected, (state) => {
        state.isFetchingGuidedCourseProgress = false;
      })
      .addCase(purchaseGuidedCourse.fulfilled, (state) => {
        if (!state.guidedCourseProgress) return;
        state.guidedCourseProgress.is_purchased = true;
        state.guidedCourseProgress.purchased_at = new Date().toISOString();
        state.guidedCourseProgress.access_expired = false;
      });
    builder
      .addCase(submitCourseReview.fulfilled, (state, action) => {
        if (!state.guidedCourseProgress) return;
        state.guidedCourseProgress.course_review = action.payload.review;
        state.guidedCourseProgress.show_review_prompt = false;
      })
      .addCase(deleteCourseReview.fulfilled, (state) => {
        if (!state.guidedCourseProgress) return;
        state.guidedCourseProgress.course_review = undefined;
        state.guidedCourseProgress.show_review_prompt = false;
      })
      .addCase(dismissCourseReview.fulfilled, (state) => {
        if (!state.guidedCourseProgress) return;
        state.guidedCourseProgress.show_review_prompt = false;
      });
    builder
      .addCase(enrollGuidedCourse.pending, (state) => {
        state.isEnrollingGuidedCourse = true;
      })
      .addCase(enrollGuidedCourse.fulfilled, (state) => {
        state.isEnrollingGuidedCourse = false;
      })
      .addCase(enrollGuidedCourse.rejected, (state) => {
        state.isEnrollingGuidedCourse = false;
      });
    builder
      .addCase(getLabelContent.pending, (state) => {
        state.isFetchingLabelContent = true;
        // Drop the outgoing lesson's cards. Without this a fetch that fails for
        // the lesson being navigated TO leaves the previous lesson's content on
        // screen under the new labelId — the learner reads one lesson and the
        // quiz they start is for another.
        state.labelContent = null;
        state.labelContentError = null;
      })
      .addCase(getLabelContent.fulfilled, (state, action) => {
        state.isFetchingLabelContent = false;
        state.labelContent = action.payload;
        state.labelContentError = null;
      })
      .addCase(getLabelContent.rejected, (state, action) => {
        state.isFetchingLabelContent = false;
        state.labelContentError =
          (action.payload as { error?: string } | undefined)?.error ??
          'label_content_failed';
      });
    builder
      .addCase(getIntroContent.pending, (state) => {
        state.isFetchingIntroContent = true;
        // Same reason the lesson fetch clears its own: a failed fetch for the
        // node being navigated TO must not leave the previous node's slides up.
        state.introContent = null;
        state.introContentError = null;
      })
      .addCase(getIntroContent.fulfilled, (state, action) => {
        state.isFetchingIntroContent = false;
        state.introContent = action.payload;
        state.introContentError = null;
      })
      .addCase(getIntroContent.rejected, (state, action) => {
        state.isFetchingIntroContent = false;
        state.introContentError =
          (action.payload as { error?: string } | undefined)?.error ??
          'intro_content_failed';
      });
    builder.addCase(completeIntro.fulfilled, (state) => {
      if (state.introContent) state.introContent.seen = true;
    });
    builder
      .addCase(completeGuidedLabel.pending, (state) => {
        state.isCompletingLabel = true;
      })
      .addCase(completeGuidedLabel.fulfilled, (state, action) => {
        state.isCompletingLabel = false;
        state.labelCompletion = action.payload;
      })
      .addCase(completeGuidedLabel.rejected, (state) => {
        state.isCompletingLabel = false;
      });
    builder.addCase(getTheoryAiChats.fulfilled, (state, action) => {
      state.theoryAIChats = action.payload;
    });
    builder
      .addCase(startGuidedLabelSession.pending, (state) => {
        state.isStartingGuidedSession = true;
      })
      .addCase(startGuidedLabelSession.fulfilled, (state, action) => {
        state.isStartingGuidedSession = false;
        state.guidedSession = action.payload;
      })
      .addCase(startGuidedLabelSession.rejected, (state) => {
        state.isStartingGuidedSession = false;
      });
    builder
      .addCase(getGuidedSessionProgress.pending, (state) => {
        state.isFetchingGuidedSessionProgress = true;
      })
      .addCase(getGuidedSessionProgress.fulfilled, (state, action) => {
        state.isFetchingGuidedSessionProgress = false;
        state.guidedSessionProgress = action.payload;
      })
      .addCase(getGuidedSessionProgress.rejected, (state) => {
        state.isFetchingGuidedSessionProgress = false;
      });
    builder
      .addCase(getGuidedCurrentQuestion.pending, (state) => {
        state.isFetchingGuidedCurrentQuestion = true;
      })
      .addCase(getGuidedCurrentQuestion.fulfilled, (state, action) => {
        state.isFetchingGuidedCurrentQuestion = false;
        state.guidedCurrentQuestion = action.payload;
      })
      .addCase(getGuidedCurrentQuestion.rejected, (state) => {
        state.isFetchingGuidedCurrentQuestion = false;
        state.guidedCurrentQuestion = null;
      });
    builder
      .addCase(attemptGuidedQuestion.pending, (state) => {
        state.isSubmittingGuidedAttempt = true;
      })
      .addCase(attemptGuidedQuestion.fulfilled, (state, action) => {
        state.isSubmittingGuidedAttempt = false;
        state.guidedAttemptResult = action.payload;
        if (state.guidedSessionProgress && action.payload?.question_id) {
          const attempt = state.guidedSessionProgress.attempts?.find(
            (a) => a.question_id === action.payload.question_id,
          );
          if (attempt) {
            attempt.is_attempt_complete = true;
            attempt.is_attempt_correct =
              action.payload.is_attempt_correct ?? undefined;
            attempt.question_skipped = action.payload.question_skipped ?? false;
            attempt.timeout = action.payload.timeout ?? false;
          }
          if (action.payload.is_last_attempt) {
            state.guidedSessionProgress.is_session_completed = true;
            state.guidedSessionProgress.target_reached =
              action.payload.target_reached;
            state.guidedSessionProgress.feedback_prompt =
              action.payload.feedback_prompt;
          }
          if (action.payload.xp_awarded != null) {
            state.guidedSessionProgress.xp_awarded = action.payload.xp_awarded;
          }
        }
      })
      .addCase(attemptGuidedQuestion.rejected, (state) => {
        state.isSubmittingGuidedAttempt = false;
      });
    builder
      .addCase(theoryAiChat.pending, (state) => {
        state.isSendingTheoryAIQuery = true;
        state.isTheoryAIWorking = true;
      })
      .addCase(theoryAiChat.fulfilled, (state, action) => {
        state.isSendingTheoryAIQuery = false;
        if (action.payload?.response && state.theoryAIChats) {
          state.theoryAIChats.chats = [
            ...(state.theoryAIChats.chats ?? []),
            action.payload.response,
          ];
        }
      })
      .addCase(theoryAiChat.rejected, (state) => {
        state.isSendingTheoryAIQuery = false;
        state.isTheoryAIWorking = false;
      });
    builder
      .addCase(startGuidedTopicSession.pending, (state) => {
        state.isStartingTopicTestSession = true;
      })
      .addCase(startGuidedTopicSession.fulfilled, (state, action) => {
        state.isStartingTopicTestSession = false;
        state.topicTestSession = action.payload;
      })
      .addCase(startGuidedTopicSession.rejected, (state) => {
        state.isStartingTopicTestSession = false;
      });
    builder
      .addCase(getTopicTestProgress.pending, (state) => {
        state.isFetchingTopicTestProgress = true;
      })
      .addCase(getTopicTestProgress.fulfilled, (state, action) => {
        state.isFetchingTopicTestProgress = false;
        state.topicTestProgress = action.payload;
      })
      .addCase(getTopicTestProgress.rejected, (state) => {
        state.isFetchingTopicTestProgress = false;
      });
    builder
      .addCase(closeTopicTestSession.pending, (state) => {
        state.isClosingTopicTestSession = true;
      })
      .addCase(closeTopicTestSession.fulfilled, (state, action) => {
        state.isClosingTopicTestSession = false;
        const xpAwarded = action.payload?.xp_awarded;
        if (xpAwarded && xpAwarded > 0 && state.guidedCourseProgress) {
          state.guidedCourseProgress.xp_earned =
            (state.guidedCourseProgress.xp_earned ?? 0) + xpAwarded;
        }
      })
      .addCase(closeTopicTestSession.rejected, (state) => {
        state.isClosingTopicTestSession = false;
      })
      .addCase(closeTopicJumpSession.pending, (state) => {
        state.isClosingTopicTestSession = true;
      })
      .addCase(closeTopicJumpSession.fulfilled, (state) => {
        state.isClosingTopicTestSession = false;
      })
      .addCase(closeTopicJumpSession.rejected, (state) => {
        state.isClosingTopicTestSession = false;
      });
  },
});

export const {
  changeSkillBuilderSetting,
  onAIAssistantSocketResponse,
  onTheoryAISocketResponse,
  resetTheoryAIChats,
  resetSkillBuilderState,
} = skillBuilderSlice.actions;

export default skillBuilderSlice.reducer;
